/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
/* eslint-disable new-cap */
import React from 'react';
import { getConfigByRect } from '@src/framework/editor/src/js/constants/index';
import Cursor from '@src/css/ppt-editor-cursor-global.less';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import LZImg from '@src/js/lzimg';
import _has from 'lodash/has';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import get from 'lodash/get';
import { getPattFillPathFromPrst } from './path-prst';
import { getPattFillPresetSize } from './fill-utils/patt-fill-utils';
import { deepClone } from '@com-m/utils/object';
import { PatterType } from './constants';
import { color2HexAndAlpha, gradientColorExtendsAlpha } from '@src/slide/infrastructure/module/independent/color/transform';
import { ImageTypes } from '@src/slide/infrastructure/module/dependent/image/constant/image-type';
import { FillMode } from '@tencent/alloy-graphics';

const COLOR_LEV1 = 0.193;
const COLOR_LEV2 = 0.398;
type OnImgLoaded = (width: number, height: number) => void;
/**
 * 背景填充处理
 */
export default class FillBackground {
    public static imageSizeMap = new Map<string, { width: number; height: number }>(); // 全局存储图片size信息，key为url
    private defaultValue = {
        color: 'none',
    };
    private fillModeMap: Map<FillMode, string> = new Map();
    private opacity = 1;
    private isTileImgShown = false;
    private mkey: string;
    private onImgLoaded: OnImgLoaded;

    public constructor(mkey: string) {
        this.mkey = mkey;
    }

    /**
     * 在getFillStyle后在调用
     * @returns {number}
     */
    public getOpacity = () => this.opacity;

    /**
     * SVG的填充背景style
     * @returns {{fill: string}}
    */
    public getFillStyle = (bgProps: any, fillMode: FillMode = FillMode.NORMAL) => {
        this.opacity = 1;
        let fillStyle = this.defaultValue.color;
        if (bgProps) {
            if (bgProps.noFill) {
                fillStyle = 'none';
            } else if (bgProps.image) {
                if (bgProps.image.tile) {
                    fillStyle = `url(#${this.getPatternId(fillMode)})`;
                    this.opacity = bgProps.image.alpha;
                } else {
                    fillStyle = 'none';
                }
            } else if (bgProps.gradFill) {
                fillStyle = `url(#${this.getPatternId(fillMode)})`;
            } else if (bgProps.solidFill?.color.srgb_color) {
                const showColor = this.getColor(bgProps.solidFill.color.srgb_color, fillMode);
                if (showColor) {
                    const colorObj = color2HexAndAlpha(showColor);

                    fillStyle = `#${colorObj.colorValue}`;
                    this.opacity = colorObj.alpha;
                }
            } else if (bgProps.pattFill) {
                fillStyle = `url(#${this.getPatternId(fillMode)})`;
            }
        }
        return fillStyle;
    };

    public checkTileImgShown = () => this.isTileImgShown;

    public renderBg(fillMode, bgProps, width, height, bgOption) {
        if (bgProps) {
            if (!fillMode) {
                fillMode = FillMode.NORMAL;
            }
            if (bgProps.gradFill) {
                return (
                    <defs>
                        {this.defineGradualFill(fillMode, bgProps.gradFill, width, height, bgOption)}
                    </defs>
                );
            } if (bgProps.image) {
                return this.renderImage(fillMode, bgProps.image, width, height, bgOption);
            }
            if (bgProps.pattFill) {
                return this.renderPattFill(fillMode, bgProps.pattFill, width, height, bgOption);
            }
        }
        return null;
    }

    private getPatternId = (fillMode: FillMode, suffix?: string): string => {
        let patternId = this.fillModeMap.get(fillMode);
        if (!patternId) {
            if (!suffix) {
                patternId = `${fillMode}_${this.mkey}`;
            } else {
                patternId = `${fillMode}_${this.mkey}_${suffix}`;
            }
            this.fillModeMap.set(fillMode, patternId);
        }
        return patternId;
    };

    private getColor(orgColor: string, fillMode: FillMode): string {
        let color = orgColor;
        switch (fillMode) {
            case FillMode.NONE:
                color = '#ffffff00'; // 背景透明
                break;
            case FillMode.NORMAL:
                break;
            case FillMode.BRIGHT:
                color = gradientColorExtendsAlpha(orgColor, 'rgb(255,255,255)', COLOR_LEV1);
                break;
            case FillMode.BRIGHTER:
                color = gradientColorExtendsAlpha(orgColor, 'rgb(255,255,255)', COLOR_LEV2);
                break;
            case FillMode.DARK:
                color = gradientColorExtendsAlpha(orgColor, 'rgb(0,0,0)', COLOR_LEV1);
                break;
            case FillMode.DARKER:
                color = gradientColorExtendsAlpha(orgColor, 'rgb(0,0,0)', COLOR_LEV2);
                break;
        }
        return color;
    }

    /**
     * 角度转换为放射渐变的圆心坐标
     */
    private angle2CirclePoint(angle) {
        const w = 1 / 2;
        const h = 1 / 2;
        const r =  0.7071;

        const radian = angle * Math.PI / 180;  // 弧度
        const x = (Math.cos(radian) * r) + w;
        const y = (Math.sin(radian) * r) + h;

        return { cx: x.toFixed(2), cy: y.toFixed(2) };
    }

    private angle2Vector(angle) {
        angle = parseInt(angle, 10);
        const gradV =  Math.sqrt(1 + 1) - 1;  // 斜边 - 直角边
        let point1 = { x: 0, y: 0 };
        let point2 = { x: 1, y: 0 };
        let radian;  // 弧度
        let v;  // 正切值
        if (angle <= 45) {
            point1 = { x: 0, y: 0 };
            radian = angle * Math.PI / 180;
            v = Math.tan(radian);
            point2 = { x: 1 + (gradV * this.getInterpolation(v)), y: v };
        } else if (angle <= 90) {
            point1 = { x: 0, y: 0 };
            const radian = (angle - 45) * Math.PI / 180;
            v = Math.tan(radian);
            point2 = { x: 1 - v, y: 1 + (gradV * this.getInterpolation(v)) };
        } else if (angle <= 135) {
            point1 = { x: 1, y: 0 };
            const radian = (angle - 90) * Math.PI / 180;
            v = Math.tan(radian);
            point2 = { x: 1 - v, y: 1 + (this.getInterpolation(v) * gradV) };
        } else if (angle <= 180) {
            const radian = (angle - 135) * Math.PI / 180;
            v = Math.tan(radian);
            point1 = { x: 1, y: 0 };
            point2 = { x: -gradV * this.getInterpolation(v), y: 1 - v };
        } else if (angle <= 225) {
            const radian = (angle - 180) * Math.PI / 180;
            v = Math.tan(radian);
            point1 = { x: 1, y: 1 };
            point2 = { x: -gradV * this.getInterpolation(v), y: 1 - v };
        } else if (angle <= 270) {
            const radian = (angle - 225) * Math.PI / 180;
            v = Math.tan(radian);
            point1 = { x: 1, y: 1 };
            point2 = { x: v, y: -gradV * this.getInterpolation(v) };
        } else if (angle <= 315) {
            const radian = (angle - 270) * Math.PI / 180;
            v = Math.tan(radian);
            point1 = { x: 0, y: 1 };
            point2 = { x: v, y: -this.getInterpolation(v) * gradV };
        } else if (angle <= 360) {
            const radian = (angle - 315) * Math.PI / 180;
            v = Math.tan(radian);
            point1 = { x: 0, y: 1 };
            point2 = { x: 1 + (gradV * this.getInterpolation(v)), y: v };
        }

        return {
            x1: point1.x, y1: point1.y, x2: point2.x, y2: point2.y,
        };
    }

    // 正弦插值器
    private getInterpolation(fraction) {
        return Number((Math.sin(fraction * Math.PI) / 2).toFixed(2));
    }

    /**
     * 获取背景平铺图片对齐方式
     * @param align
     * @param imgW  图片size
     * @param imgH
     * @param width 容器大小
     * @param height
     */
    private getTileAlignLoc(tileObj, imgW, imgH, width, height) {
        let x = 0;
        let y = 0;
        const align = tileObj.algn || 'tl';

        if (imgW === 0 || imgH === 0 || width === 0 || height === 0) return { x, y };

        // 靠4个角
        const offsetX = imgW > width ? (width - imgW) : width % imgW;
        const offsetY = imgH > height ? (height - imgH) : height % imgH;

        // 居中的offset
        const centerOffsetX = this.getCenterOffsetX(imgW, width);

        const centerOffsetY = this.getCenterOffsetY(imgH, height);

        const xyObj = this.getTileXY(align, offsetX, offsetY, centerOffsetX, centerOffsetY);
        x = xyObj.x;
        y = xyObj.y;

        if (tileObj.tx) {
            x += tileObj.tx;
        }
        if (tileObj.ty) {
            y += tileObj.ty;
        }

        return { x, y };
    }

    private getCenterOffsetX(imgW, width) {
        let centerOffsetX = 0;
        if (imgW > width) {
            centerOffsetX = (width - imgW) / 2;
        } else {
            let startIndex = 1; // 奇数
            while (startIndex * imgW < width) {
                startIndex += 2;
            }
            centerOffsetX = (width - (startIndex * imgW)) / 2;
        }
        return centerOffsetX;
    }

    private getCenterOffsetY(imgH, height) {
        let centerOffsetY = 0;
        if (imgH > height) {
            centerOffsetY = (height - imgH) / 2;
        } else {
            let startIndex = 1;// 奇数
            while (startIndex * imgH < height) {
                startIndex += 2;
            }
            centerOffsetY = (height - (startIndex * imgH)) / 2;
        }
        return centerOffsetY;
    }

    private getTileXY(align, offsetX, offsetY, centerOffsetX, centerOffsetY) {
        let x = 0;
        let y = 0;
        switch (align) {
            case 'tl':     // 左对齐
                break;
            case 'tr':     // 右对齐
                x = offsetX;
                break;
            case 'bl':     // 左下对齐
                y = offsetY;
                break;
            case 'br':     // 右下对齐
                x = offsetX;
                y = offsetY;
                break;
            case 't':     // 靠上
                x = centerOffsetX;
                break;
            case 'b':     // 靠下
                x = centerOffsetX;
                y = offsetY;
                break;
            case 'l':     // 靠左
                y = centerOffsetY;
                break;
            case 'r':     // 靠右
                x = offsetX;
                y = centerOffsetY;
                break;
            case 'ctr':    // 居中
                x = centerOffsetX;
                y = centerOffsetY;
                break;
        }
        return { x, y };
    }

    private getImgTileFlipInfo(tile, imgW, imgH) {
        const { flip } = tile;
        const { algn } = tile;

        const doubleW = imgW * 2;
        const doubleH = imgH * 2;

        const flipInfo = {
            pattern: {
                width: imgW,
                height: imgH,
                translateX: 0,
                translateY: 0,
            },
            imageArray: [{
                w: imgW, h: imgH, x: 0, y: 0, transform: '',
            }],
        };
        const imgArray = flipInfo.imageArray;
        const p = flipInfo.pattern;

        switch (flip) {
            case 'x':
                p.width = doubleW;
                this.setFlipXTransX(p, algn, imgW);
                imgArray.push({
                    w: imgW + 1, h: imgH, x: -doubleW, y: 0, transform: 'scale(-1,1)',
                }); // //加1是为了解决缩放的时候，可能产生的空隙
                break;
            case 'y':
                p.height = doubleH;
                this.setFlipYTransY(p, algn, imgH);

                imgArray.push({
                    w: imgW, h: imgH + 1, x: 0, y: -doubleH, transform: 'scale(1,-1)',
                });
                break;
            case 'xy':
                p.width = doubleW;
                p.height = doubleH;
                this.setTileImgPatternXY(p, algn, imgW, imgH);
                imgArray.push({
                    w: imgW + 1, h: imgH, x: -doubleW, y: 0, transform: 'scale(-1,1)',
                });
                imgArray.push({
                    w: imgW, h: imgH + 1, x: 0, y: -doubleH, transform: 'scale(1,-1)',
                });
                imgArray.push({
                    w: imgW + 1, h: imgH + 1, x: -doubleW, y: -doubleH, transform: 'scale(-1,-1)',
                });
                break;
        }

        return flipInfo;
    }
    private setTileImgPatternXY(p, algn, imgW, imgH) {
        this.setFlipXTransX(p, algn, imgW);
        this.setFlipYTransY(p, algn, imgH);
    }

    private setFlipXTransX(p, algn, imgW) {
        if (algn === 'tr' || algn === 'br' || algn === 'r') {
            p.translateX = imgW;
        } else if (algn === 't' || algn === 'b' || algn === 'ctr') {
            p.translateX = imgW / 2;
        }
    }

    private setFlipYTransY(p, algn, imgH) {
        if (algn === 'bl' || algn === 'br' || algn === 'b') {
            p.translateY = imgH;
        } else if (algn === 'l' || algn === 'r' || algn === 'ctr') {
            p.translateY = imgH / 2;
        }
    }

    private defineGradualFill = (fillMode, gradFill, width, height, bgOption) => {
        const { rotate } = bgOption;
        const { flipH } = bgOption;
        const { flipV } = bgOption;
        if (!(typeof (bgOption.scaleW) === 'undefined')) {
            width = width / bgOption.scaleW;
        }
        if (!(typeof (bgOption.scaleH) === 'undefined')) {
            height = height / bgOption.scaleH;
        }
        if (gradFill.pathGradient && gradFill.pathGradient.path === 'linear') {
            let angle = 0;
            if (gradFill.lin) {
                angle = gradFill.lin.ang || 0;
            }
            angle = this.getLinearAngle(gradFill, rotate, angle, flipH, flipV);
            const rect = this.angle2Vector(angle);
            return (
                <linearGradient gradientUnits="userSpaceOnUse" id={this.getPatternId(fillMode)} x1={rect.x1 * width} y1={rect.y1 * height} x2={rect.x2 * width} y2={rect.y2 * height}>
                    {gradFill.gsList.map((item, index) => {
                        let bgColor = '#ffffff00';
                        if (item?.color) {
                            if (!_has(item.color, 'srgb_color')) {
                                bgColor = item.color.backup_rgba;
                            } else {
                                bgColor = item.color.srgb_color;
                            }
                        }
                        const showColor = this.getColor(bgColor, fillMode);
                        let colorValue = '#ffffff';
                        let opacity = 0;
                        if (showColor) {
                            const colorObj = color2HexAndAlpha(showColor);
                            colorValue = `#${colorObj.colorValue}`;
                            opacity = colorObj.alpha;
                        }
                        const pos = item ? item.pos : 0;
                        const key = index;

                        return <stop offset={`${pos}%`} stopColor={colorValue} key={key} stopOpacity={opacity}/>;
                    })}
                </linearGradient>
            );
        }
        const hasRotateWithShape = typeof (gradFill.rotateWithShape) !== 'undefined';
        const notRotateWithShape = hasRotateWithShape && gradFill.rotateWithShape === 0; // 不随形状旋转
        let val;
        if (notRotateWithShape) { // 不随形状旋转
            const direct = getConfigByRect(gradFill.pathGradient.fillToRect);
            let { angle } = direct;
            if (angle) { // 不居中
                angle = this.getRadialAngle(rotate, angle, flipH, flipV);
                const point = this.angle2CirclePoint(angle);
                val = { cx: point.cx, cy: point.cy, r: 2 };
            } else {  // 居中
                val = direct.val;
            }
        } else {
            val = getConfigByRect(gradFill.pathGradient.fillToRect).val;
        }

        const size = Math.max(width, height);
        const r = size * 6 / 10;
        const v = (
            <radialGradient gradientUnits="userSpaceOnUse" id={this.getPatternId(fillMode)} cx={val.cx * width} cy={val.cy * height} r={val.r * r}>
                {gradFill.gsList.map((item, index) => {
                    let bgColor = '#ffffff00';
                    if (item?.color) {
                        if (!_has(item.color, 'srgb_color')) {
                            bgColor = item.color.backup_rgba;
                        } else {
                            bgColor = item.color.srgb_color;
                        }
                    }
                    const showColor = this.getColor(bgColor, fillMode);
                    let colorValue = '#ffffff';
                    let opacity = 0;
                    if (showColor) {
                        const colorObj = color2HexAndAlpha(showColor);
                        colorValue = `#${colorObj.colorValue}`;
                        opacity = colorObj.alpha;
                    }
                    const pos = item ? item.pos : 0;
                    const key = index;

                    return <stop offset={`${pos}%`} stopColor={colorValue} key={key} stopOpacity={opacity}/>;
                })}
            </radialGradient>
        );
        return v;
    };

    private getRadialAngle(rotate, angle, flipH, flipV) {
        if (rotate && rotate > 0) {
            angle = angle - rotate > 0 ? angle - rotate : 360 + (angle - rotate);
        }
        if (flipH) { // 角度左右对称
            if (angle <= 180) {
                angle = 180 - angle;
            } else if (angle <= 360) {
                angle = 540 - angle;
            }
        }
        if (flipV) {
            angle = 360 - angle;  // 角度上下对称
        }
        return angle;
    }

    private getLinearAngle(gradFill, rotate, angle, flipH, flipV) {
        const hasRotateWithShape = typeof (gradFill.rotateWithShape) !== 'undefined';
        const notRotateWithShape = hasRotateWithShape && gradFill.rotateWithShape === 0; // 不随形状旋转
        if (notRotateWithShape) { // 不随形状旋转
            if (rotate && rotate > 0) {
                angle = angle - rotate > 0 ? angle - rotate : 360 + (angle - rotate);
            }
            if (flipH) { // 角度左右对称
                if (angle <= 180) {
                    angle = 180 - angle;
                } else if (angle <= 360) {
                    angle = 540 - angle;
                }
            }
            if (flipV) {
                angle = 360 - angle;  // 角度上下对称
            }
        }
        return angle;
    }

    private renderImage = (fillMode, image, width, height, bgOption) => {
        this.isTileImgShown = false;
        if (image.tile) { // 渲染平铺图片
            return this.renderTileImage(fillMode, image, width, height, bgOption);
        }

        const { path } = bgOption;
        const pointerEvents = bgOption.pointerEvents ? bgOption.pointerEvents : 'auto';
        const { cursor } = bgOption;
        const { transformStr } = bgOption;
        const { original } = bgOption;
        const { imageFiltersId } = bgOption;

        const imageUrl = image.src;
        const backupImageUrl = image.backupSrc;
        const { imageType } = image;

        let syncLoad = false;
        if (get(image, 'syncLoad')) {
            syncLoad = true;
        }

        const outerWidth = width;
        const outerHeight = height;
        const ret = this.getImageRect(image, width, height, outerWidth, outerHeight);
        const { pl } = ret;
        const { pt } = ret;
        width = ret.width;
        height = ret.height;

        const scaleW = 1;
        const scaleH = 1;

        let imgW = Math.round(width * scaleW); // 图片大小取整，否则会非常模糊
        let imgH = Math.round(height * scaleH);

        let imgLeft = Math.round(pl * scaleW);
        let imgTop = Math.round(pt * scaleH);

        const id = `${this.getPatternId(fillMode, PatterType.CLIP_PATH)}`;

        const hasRotateWithShape = typeof (image.rotWithShape) !== 'undefined';
        const notRotateWithShape = hasRotateWithShape && image.rotWithShape === 0; // 不随形状旋转
        let rotate = 0;
        const style: React.CSSProperties = {};
        if (notRotateWithShape && !image.isForeground) {
            rotate = bgOption.rotate;
            rotate = 360 - (rotate % 360);

            const newRect = getFrameAfterRotate({
                top: 0, left: 0, width: imgW, height: imgH,
            }, 360 - rotate);
            imgW = newRect.width;
            imgH = newRect.height;
            imgLeft = imgLeft + newRect.left;
            imgTop = imgTop + newRect.top;
            const transform = `rotate(${rotate}deg)`;
            style.transform = transform;
            style.transformOrigin = 'center';
        }

        const isRenderBackupImage = (imageType === ImageTypes.GIF) && backupImageUrl;
        return (
            <React.Fragment>
                <clipPath id={id}><path d={path} transform={transformStr}></path></clipPath>
                <g clipPath={`url(#${id})`} opacity={image.alpha}
                    className={Cursor[cursor]}
                    pointerEvents={pointerEvents}>
                    {
                        // GIF 图片且有首帧图的时候，渲染两张图片，一张 GIF 图，一张首帧图。
                        // 在侧边栏 clone 的时候，会将 GIF 图移除，只保留首帧图。
                        isRenderBackupImage
                            ? <React.Fragment>
                                <LZImg filter={imageFiltersId} preserveAspectRatio={'none'} syncLoad={syncLoad} xlinkHref={backupImageUrl} width={imgW} height={imgH} x={imgLeft} y={imgTop} playGif={bgOption.playGif} onImgLoaded={this.onImgLoaded} style={{ ...style, visibility: 'hidden' }} className={'need-show-clone'} original={original}/>
                                <LZImg filter={imageFiltersId} preserveAspectRatio={'none'} syncLoad={syncLoad} xlinkHref={imageUrl} width={imgW} height={imgH} x={imgLeft} y={imgTop} playGif={bgOption.playGif} onImgLoaded={this.onImgLoaded} style={style} original={original}/>
                            </React.Fragment>
                            : <LZImg filter={imageFiltersId} preserveAspectRatio={'none'} syncLoad={syncLoad} xlinkHref={imageUrl} width={imgW} height={imgH} x={imgLeft} y={imgTop} playGif={bgOption.playGif} onImgLoaded={this.onImgLoaded} style={style} original={original}/>
                    }
                    <path fill={'transparent'} d={path} transform={transformStr}
                        // 让firefox该区域可以选择editor文字。
                        onMouseDown={e => e.preventDefault() }
                        style={{
                            userSelect: 'text', MozUserSelect: 'text', WebkitUserSelect: 'text',
                        }}/>
                </g>
            </React.Fragment>
        );
    };

    /**
     * 渲染图案填充
     * @param fillMode
     * @param pattFill fillData数据
     * @param width
     * @param height
     * @param bgOption 翻转旋转数据
     * @returns {JSX.Element}
     */
    private renderPattFill(fillMode, pattFill, width, height, bgOption) {
        const { scaleW, scaleH } = this.getScaleInfo(bgOption);
        const { flipH, flipV, rotate } = bgOption;

        width = width / scaleW;
        height = height / scaleH;

        let rorTransX = 0;
        let rorTransY = 0;
        if (rotate > 0) {
            const oldRect = {
                left: 0, top: 0, width, height,
            };
            const newRect = getFrameAfterRotate(oldRect, rotate);
            rorTransX = newRect.left;
            rorTransY = newRect.top;
        }

        const { preset, fgColor, bgColor } = deepClone(pattFill); // 防止修改model数据
        fgColor.color.srgb_color = this.getColor(fgColor.color.srgb_color, fillMode);
        bgColor.color.srgb_color = this.getColor(bgColor.color.srgb_color, fillMode);
        // 获取svg重复单元
        const singleSvgRepeatEle = getPattFillPathFromPrst(preset, fgColor, bgColor, scaleW, scaleH);
        // patten宽高
        const { patternWidth, patternHeight } = getPattFillPresetSize(singleSvgRepeatEle, scaleW, scaleH);

        // 不随形状旋转transform
        const patternTransform = `rotate(${-rotate},${width / 2},${height / 2}) scale(${flipV ? -1 : 1},${flipH ? -1 : 1})`;

        const patternOffsetX = Math.round(rorTransX);
        const patternOffsetY = Math.round(rorTransY);

        return (
            <defs>
                <pattern id={this.getPatternId(fillMode)}
                    patternUnits="userSpaceOnUse"
                    width={patternWidth}
                    height={patternHeight}
                    x={patternOffsetX}
                    y={patternOffsetY}
                    patternTransform={patternTransform}
                    mode="pattFill"
                >
                    {singleSvgRepeatEle}
                </pattern>
            </defs>
        );
    }

    private getImageRect(image, width, height, outerWidth, outerHeight) {
        let pl = 0;
        let pt = 0;
        let pr = 0;
        let pb = 0;

        if (typeof image.width !== 'undefined') {   // /说明是前景图
            width = image.width;
            height = image.height;
            pl = parseInt(image.pl, 10);
            pt = parseInt(image.pt, 10);
        } else if (image.stretch) {   // /说明是背景图
            if (image.stretch.srcRect) {
                pl = width * safeValue(image.stretch.srcRect.l, 0);
                pr = width * safeValue(image.stretch.srcRect.r, 0);
                pt = height * safeValue(image.stretch.srcRect.t, 0);
                pb = height * safeValue(image.stretch.srcRect.b, 0);
            } else if (image.stretch.fillRect) {
                pl = width * safeValue(image.stretch.fillRect.l, 0);
                pr = width * safeValue(image.stretch.fillRect.r, 0);
                pt = height * safeValue(image.stretch.fillRect.t, 0);
                pb = height * safeValue(image.stretch.fillRect.b, 0);
            }

            width = width + pl + pr;
            height = height + pt + pb;
            if (isNaN(width)) width = outerWidth;
            if (isNaN(height)) height = outerHeight;
            pl = -pl;
            pt = -pt;
        }
        return { pl, pt, width, height };
    }

    /**
     * 渲染平铺的图片，和单个图片渲染方式不一样
     * @param fillMode
     * @param image
     * @param width
     * @param height
     * @param bgOption
     */
    private renderTileImage = (fillMode, image, width, height, bgOption) => {
        const imageUrl = image.src;

        const { scaleW, scaleH } = this.getScaleInfo(bgOption);
        const { flipH, flipV } = bgOption;

        const imageSize = FillBackground.imageSizeMap.get(imageUrl);
        let { imgW, imgH } = this.getSize(imageSize);

        width = width / scaleW;
        height = height / scaleH;

        const oldWidth = width;
        const oldHeight = height;

        let callBack: OnImgLoaded;

        if (imgW === 0 || imgH === 0) {
            callBack = this.onImgLoaded;
        } else {
            this.isTileImgShown = true;
            const imgObj = this.setImageWH(image, imgW, imgH);
            imgW = imgObj.imgW;
            imgH = imgObj.imgH;
        }

        // 不随形状旋转计算
        const hasRotateWithShape = typeof (image.rotWithShape) !== 'undefined';
        const notRotateWithShape = hasRotateWithShape && image.rotWithShape === 0; // 不随形状旋转
        const { rotate } = bgOption;
        const { original } = bgOption;
        let isRotateWithShape = false;
        let rorTransX = 0;
        let rorTransY = 0;
        if (notRotateWithShape && rotate && rotate > 0) {  // 如果发现是不随形状旋转
            isRotateWithShape = true;
            const oldRect = {
                left: 0, top: 0, width, height,
            };
            const newRect = getFrameAfterRotate(oldRect, rotate);
            rorTransX = newRect.left;
            rorTransY = newRect.top;
            width = newRect.width;
            height = newRect.height;
        }

        imgW = ptToCanvas(imgW) / scaleW;
        imgH = ptToCanvas(imgH) / scaleH;

        const syncLoad = this.getSyncLoad(image);

        const { patternWidth, patternHeight, imgRenderArray, patternTransform } = this.
            computeImageInfo(imgW, imgH, image, isRotateWithShape, rotate, oldWidth, oldHeight, flipH, flipV);

        const patternLoc = this.getTileAlignLoc(image.tile, patternWidth, patternHeight, width, height);
        const patternOffsetX = Math.round(patternLoc.x + rorTransX);
        const patternOffsetY = Math.round(patternLoc.y + rorTransY);

        return (
            <defs>
                <pattern id={this.getPatternId(fillMode)}
                    patternUnits="userSpaceOnUse"
                    width={patternWidth}
                    height={patternHeight}
                    x={patternOffsetX}
                    y={patternOffsetY}
                    patternTransform={patternTransform}
                >
                    {imgRenderArray.map((item, index) => <LZImg
                        key={index}
                        preserveAspectRatio={'none'}
                        syncLoad={syncLoad}
                        xlinkHref={imageUrl}
                        width={item.w}
                        height={item.h}
                        x={item.x}
                        y={item.y}
                        transform={item.transform}
                        playGif={bgOption.playGif}
                        onImgLoaded={callBack}
                        original={original}/>)
                    }
                </pattern>
            </defs>
        );
    };

    private getSize(imageSize) {
        const imgW = imageSize ? imageSize.width : 0;
        const imgH = imageSize ? imageSize.height : 0;
        return { imgW, imgH };
    }

    private getScaleInfo(bgOption) {
        const scaleW = bgOption.scaleW || 1;
        const scaleH = bgOption.scaleH || 1;
        return { scaleW, scaleH };
    }

    private getSyncLoad(image) {
        let syncLoad = false;
        if (image?.syncLoad) {
            syncLoad = true;
        }
        return syncLoad;
    }

    private setImageWH(image, imgW, imgH) {
        if (image.tile.sx) {
            imgW = Math.round(imgW * image.tile.sx / 100);
        }
        if (image.tile.sx) {
            imgH = Math.round(imgH * image.tile.sy / 100);
        }
        return { imgW, imgH };
    }

    private computeImageInfo(imgW, imgH, image, isRotateWithShape, rotate, oldWidth, oldHeight, flipH, flipV) {
        const tileFlipInfo = this.getImgTileFlipInfo(image.tile, imgW, imgH);
        const imgRenderArray = tileFlipInfo.imageArray;

        const { pattern } = tileFlipInfo;
        const patternWidth = pattern.width;
        const patternHeight = pattern.height;

        const flip = image.tile.flip || '';
        const algn = image.tile.algn || '';

        let patternTransform = '';
        if (isRotateWithShape) {
            patternTransform += `rotate(${-rotate},${oldWidth / 2},${oldHeight / 2}) scale(${flipV ? -1 : 1},${flipH ? -1 : 1})`;
        }
        return { patternWidth, patternHeight, flip, algn, imgRenderArray, patternTransform, pattern };
    }
}

function safeValue(value, defaultValue) {
    return value ? value : defaultValue;
}
